import { offeringFamily, type Manifest, type OfferingLike } from "@nodus/ui";
import { quantityBytes } from "../storage/model";
import { spotQuote } from "./spot-hardware";
import { researchTerms } from "../workspaces/research-capacity";

export type ComputeSelection = { cpu?: string; memory?: string; gpu?: string; exact?: boolean; count?: number; placement?: "automatic"; region?: string; offering?: string; minMemory?: string; interruptible?: "Allow" | "Never" };
export type ComputeSpec = {
  resources?: { gpu?: { type?: string[]; exact?: boolean; count?: number; minMemory?: string }; cpu?: string; memory?: string };
  placement?: { regions?: string[]; offerings?: string[]; pool?: string; interruptible?: string };
};

/** A catalog's unspecified location is not a customer region restriction. */
export function placementRegion(region?: string): string | undefined {
  return region && region !== "unknown" && region !== "any" ? region : undefined;
}

/** Search values describe API constraints, never a client-side allocation or an availability guarantee. */
export function computeSearch(s: Record<string, unknown>): ComputeSelection {
  const count = Number(s.count);
  const name = (v: unknown) => typeof v === "string" && /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,252}$/.test(v) ? v : undefined;
  return {
    ...(typeof s.cpu === "string" && /^\d+(?:\.\d+)?$/.test(s.cpu) ? { cpu: s.cpu } : {}),
    ...(typeof s.memory === "string" && /^\d+(?:\.\d+)?(?:Gi|Mi|G|M)$/.test(s.memory) ? { memory: s.memory } : {}),
    ...(name(s.gpu) ? { gpu: name(s.gpu)! } : {}),
    ...([1, 2, 4, 8].includes(count) ? { count } : {}),
    ...(s.interruptible === "Allow" || s.interruptible === "Never" ? { interruptible: s.interruptible } : {}),
    ...(s.placement === "automatic" ? { placement: "automatic" as const } : {}),
    ...(placementRegion(name(s.region)) ? { region: placementRegion(name(s.region)) } : {}),
    ...(name(s.offering) ? { offering: name(s.offering)! } : {}),
    ...(typeof s.minMemory === "string" && /^\d+(?:\.\d+)?(?:Gi|Mi|G|M)$/.test(s.minMemory) ? { minMemory: s.minMemory } : {}),
  };
}

export function applyComputeSelection(manifest: Manifest, selection?: ComputeSelection): Manifest {
  if (!selection?.gpu && selection?.placement !== "automatic") return manifest;
  const next = structuredClone(manifest);
  const spec = next.spec as ComputeSpec;
  const resources = { ...spec.resources };
  if (selection.gpu === "CPU") {
    delete resources.gpu;
    resources.cpu ??= "2";
    resources.memory ??= "4Gi";
  } else {
    resources.gpu = {
      ...(selection.gpu ? { type: [selection.gpu] } : {}),
      ...(selection.exact ? { exact: true } : {}),
      ...(selection.placement === "automatic" ? { minMemory: selection.minMemory ?? "24Gi" } : {}),
      count: selection.count ?? 1,
      ...(selection.minMemory ? { minMemory: selection.minMemory } : {}),
    };
  }
  if (selection.cpu) resources.cpu = selection.cpu;
  if (selection.memory) resources.memory = selection.memory;
  spec.resources = resources;
  const placement = { ...spec.placement };
  if (selection.offering && selection.placement !== "automatic") delete placement.pool;
  delete placement.offerings;
  delete placement.regions;
  if (selection.offering && selection.placement !== "automatic") placement.offerings = [selection.offering];
  const region = placementRegion(selection.region);
  if (region) placement.regions = [region];
  if (selection.interruptible) placement.interruptible = selection.interruptible;
  spec.placement = placement;
  return next;
}

export function computeFromSpec(spec: ComputeSpec): ComputeSelection {
  const gpu = spec.resources?.gpu;
  return { ...(gpu ? gpu.type?.length ? { gpu: gpu.type[0], ...(gpu.exact ? { exact: true } : {}) } : { placement: "automatic" as const } : {
    gpu: "CPU",
    ...(!spec.placement?.offerings?.length ? { placement: "automatic" as const } : {}),
  }),
    count: gpu?.count ?? 1, minMemory: gpu?.minMemory, offering: spec.placement?.offerings?.[0], region: spec.placement?.regions?.[0],
    cpu: spec.resources?.cpu, memory: spec.resources?.memory,
    ...(spec.placement?.interruptible === "Allow" || spec.placement?.interruptible === "Never" ? { interruptible: spec.placement.interruptible } : {}) };
}

/** Preserve the exact offering and its constraints from browsing through launch. */
export function selectionForOffering(offering: OfferingLike): ComputeSelection {
  const spec = offering.spec;
  const region = placementRegion(spec?.regionClass);
  return {
    gpu: spec?.category === "CPU" ? "CPU" : spec?.accelerator?.id ?? offeringFamily(offering),
    count: spec?.count || 1,
    offering: offering.metadata.name,
    interruptible: spec?.interruptible ? "Allow" : "Never",
    ...(region ? { region } : {}),
    ...(spec?.accelerator?.memory ? { minMemory: spec.accelerator.memory } : {}),
    ...(spec?.host?.cpu ? { cpu: String(spec.host.cpu) } : {}),
    ...(spec?.host?.memory ? { memory: spec.host.memory } : {}),
  };
}

/** Missing capability flags keep older catalogs usable; an explicit unsupported kind is never selectable. */
export function supportsCompute(offering: OfferingLike, target: "jobs" | "workspaces" | "sandboxes"): boolean {
  const spec = offering.spec as (OfferingLike["spec"] & { supports?: { jobs?: boolean; workspaces?: boolean; sandboxes?: boolean } });
  const disk = quantityBytes(spec?.host?.disk);
  const cpu = Number(spec?.host?.cpu ?? 2);
  return spec?.supports?.[target] !== false && (target !== "workspaces" ||
    ((disk === undefined || disk >= 80 * 2 ** 30) && (spec?.category !== "CPU" || (cpu >= 2 && cpu <= 32))));
}

/** Limited may mean unreported supply; immediate launches need a positive observed machine count. */
export function workspaceOfferingInStock(offering: OfferingLike): boolean {
  const quote = spotQuote(offering);
  if (quote) return quote.available_to_quote;
  const status = offering.status;
  return Number.isFinite(status?.availableNow) && (status?.availableNow ?? 0) > 0 &&
    (status?.availability === "Available" || status?.availability === "Limited");
}

/** Reviewed research requests may queue; they do not require confirmed immediate stock. */
export function workspaceOfferingLaunchable(offering: OfferingLike): boolean {
  return workspaceOfferingInStock(offering) || Boolean(researchTerms(offering) &&
    offering.status?.availability === "Limited" && offering.status.availableNow === 0);
}
